/* src/ATS/components/ResumeAnalyzer.js */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  X, 
  AlertCircle, 
  CheckCircle, 
  ChevronRight, 
  Zap, 
  Settings, 
  ShieldCheck, 
  Layers, 
  Briefcase, 
  ArrowLeft 
} from 'lucide-react';
import '../styles/ResumeAnalyzer.css';
import BASE_URL from '../../url';

export default function ResumeAnalyzer({ initialJobDescription = '', onCancel }) {
  // --- STATE ---
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(() => sessionStorage.getItem('ats_ra_fileName') || '');
  const [jobDescription, setJobDescription] = useState(() => sessionStorage.getItem('ats_ra_jobDescription') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(() => {
    const saved = sessionStorage.getItem('ats_ra_results');
    return saved ? JSON.parse(saved) : null;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  
  // Tabs and Section selection
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('ats_ra_activeTab') || 'report'); // 'report', 'jd'
  const [activeCategory, setActiveCategory] = useState(() => sessionStorage.getItem('ats_ra_activeCategory') || 'searchability'); // 'searchability', 'hardskills', 'softskills', 'tips'
  const [skillsTab, setSkillsTab] = useState(() => sessionStorage.getItem('ats_ra_skillsTab') || 'comparison'); // 'comparison', 'highlighted'

  // Job Search State
  const [jobs, setJobs] = useState([]);
  const [jobSearch, setJobSearch] = useState(() => sessionStorage.getItem('ats_ra_jobSearch') || '');
  const [showJobDropdown, setShowJobDropdown] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('ats_ra_jobDescription', jobDescription);
    if (results) {
      sessionStorage.setItem('ats_ra_results', JSON.stringify(results));
    } else {
      sessionStorage.removeItem('ats_ra_results');
    }
    sessionStorage.setItem('ats_ra_activeTab', activeTab);
    sessionStorage.setItem('ats_ra_activeCategory', activeCategory);
    sessionStorage.setItem('ats_ra_skillsTab', skillsTab);
    sessionStorage.setItem('ats_ra_jobSearch', jobSearch);
    sessionStorage.setItem('ats_ra_fileName', fileName);
  }, [jobDescription, results, activeTab, activeCategory, skillsTab, jobSearch, fileName]);
  
  const dropdownRef = useRef(null);
  const reportRef = useRef(null);

  // Fetch jobs on mount
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/recruitment/public/roles`);
        if (response.ok) {
          let data = await response.json();
          data = Array.isArray(data) ? data : (data.value || []);
          setJobs(data);
        }
      } catch (err) {
        console.error('Failed to fetch jobs for analyzer', err);
      }
    };
    fetchJobs();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowJobDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredJobs = jobs.filter(job => {
    const search = jobSearch.toLowerCase();
    const jobId = String(job.jobId || '').toLowerCase();
    const sysId = String(job.systemId || `JOB-${String(job.id).padStart(5, '0')}`).toLowerCase();
    const title = String(job.role || '').toLowerCase();
    return search === '' || jobId.includes(search) || sysId.includes(search) || title.includes(search);
  });

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  useEffect(() => {
    if (initialJobDescription) {
      setJobDescription(stripHtml(initialJobDescription));
    }
  }, [initialJobDescription]);

  const handleSelectJob = (job) => {
    setJobSearch(job.jobId || job.id || job.role || '');
    const rawDesc = job.description || job.jobDescription || '';
    setJobDescription(stripHtml(rawDesc));
    setShowJobDropdown(false);
  };

  // --- HELPERS ---
  const highlightText = (text, skills) => {
    if (!text) return null;
    let parts = [text];
    
    // Sort skills by length to avoid highlighting substrings first
    const sortedSkills = [...skills].sort((a, b) => b.skill.length - a.skill.length);
    
    sortedSkills.forEach(s => {
      const newParts = [];
      const regex = new RegExp(`(${s.skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      
      parts.forEach(part => {
        if (typeof part !== 'string') {
          newParts.push(part);
          return;
        }
        
        const split = part.split(regex);
        split.forEach((subPart, i) => {
          if (subPart && subPart.toLowerCase() === s.skill.toLowerCase()) {
            newParts.push(
              <span key={`${s.skill}-${i}`} style={{ backgroundColor: s.resumeCount > 0 ? '#eff6ff' : '#fee2e2', color: s.resumeCount > 0 ? '#1d4ed8' : '#991b1b', padding: '0 2px', borderRadius: '2px', fontWeight: '500' }}>
                {subPart}
              </span>
            );
          } else if (subPart) {
            newParts.push(subPart);
          }
        });
      });
      parts = newParts;
    });
    
    return parts;
  };

  // --- HANDLERS ---
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (file) => {
    const allowed = ['.pdf', '.doc', '.docx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      setError('Invalid file type. Please upload PDF, DOC, or DOCX.');
      return;
    }
    setFile(file);
    setFileName(file.name);
    setError('');
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError('Please upload a resume first.');
      return;
    }
    if (!jobDescription.trim()) {
      setError('Please paste a job description.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('resume', file);
      formData.append('jobDescription', jobDescription);

      const response = await fetch(`${BASE_URL}/api/ats/analyze-unified`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to analyze resume');
      }

      setResults(data.data);
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'An error occurred during analysis');
    } finally {
      setLoading(false);
    }
  };

  const resetAnalyzer = () => {
    setResults(null);
    setFile(null);
    setFileName('');
    setJobDescription('');
    setError('');
  };

  const handleCancel = () => {
    setFile(null);
    setFileName('');
    setError('');
    sessionStorage.removeItem('ats_ra_fileName');
    if (typeof onCancel === 'function') {
      onCancel();
    } else {
      setJobDescription(initialJobDescription || '');
      setJobSearch('');
      sessionStorage.removeItem('ats_ra_jobSearch');
      sessionStorage.removeItem('ats_ra_jobDescription');
    }
  };

  // Handlers for auto-fix and download were removed
  
  const exportToWord = () => {
    if (!reportRef.current) return;
    
    const content = reportRef.current.innerHTML;
    const preHtml = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Resume Recommendations</title></head><body>";
    const postHtml = "</body></html>";
    const html = preHtml + content + postHtml;

    const blob = new Blob(['\ufeff', html], {
        type: 'application/msword'
    });
    
    const url = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(html);
    const filename = 'Resume_Recommendations.doc';
    
    const downloadLink = document.createElement("a");
    document.body.appendChild(downloadLink);
    
    if (navigator.msSaveOrOpenBlob) {
        navigator.msSaveOrOpenBlob(blob, filename);
    } else {
        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.click();
    }
    document.body.removeChild(downloadLink);
  };

  // --- RENDERING HELPERS ---
  
  const renderInputView = () => (
    <div className="analyzer-input-card">
      <div className="analyzer-input-grid">
        {/* Resume Upload */}
        <div className="input-group">
          <label className="input-label">
            <FileText size={18} color="#3b5bfd" />
            Upload Resume
          </label>
          
          {(!file && !fileName) ? (
            <div 
              className={`drop-zone ${isDragging ? 'active' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => document.getElementById('resume-upload').click()}
            >
              <Upload size={32} className="drop-zone-icon" />
              <p className="drop-zone-text">
                Drag and drop your resume here, or <span>browse</span>
              </p>
              <p style={{ fontSize: '11px', color: '#9ca3af' }}>PDF, DOC, DOCX up to 5MB</p>
              <input 
                id="resume-upload"
                type="file" 
                hidden 
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
              />
            </div>
          ) : (
            <div className="file-preview">
              <FileText size={20} color="#3b5bfd" />
              <div className="file-info">{fileName}</div>
              <X 
                size={18} 
                className="remove-file" 
                onClick={() => { setFile(null); setFileName(''); }} 
              />
            </div>
          )}
        </div>

        {/* JD Input */}
        <div className="input-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label className="input-label" style={{ marginBottom: 0 }}>
              <Briefcase size={18} color="#3b5bfd" />
              Job Description
            </label>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <input 
                type="text" 
                placeholder="Search by Job ID or Title..." 
                value={jobSearch}
                onChange={(e) => {
                  setJobSearch(e.target.value);
                  setShowJobDropdown(true);
                }}
                onFocus={() => setShowJobDropdown(true)}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '350px', outline: 'none' }}
              />
              {showJobDropdown && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', width: '350px', maxHeight: '250px', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', zIndex: 10 }}>
                  {filteredJobs.length > 0 ? filteredJobs.map(job => (
                    <div 
                      key={job.id} 
                      style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px', transition: 'background-color 0.15s ease' }}
                      onClick={() => handleSelectJob(job)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                    >
                      <div style={{ fontWeight: '600', color: '#111827' }}>{job.role}</div>
                      <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>External Job Ref: {job.jobId || 'N/A'} | System ID: {job.systemId || `JOB-${String(job.id).padStart(5, '0')}`}</div>
                    </div>
                  )) : <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>No matching jobs found.</div>}
                </div>
              )}
            </div>
          </div>
          <textarea 
            className="jd-textarea"
            placeholder="Paste the job description here..."
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="ats-error-banner" style={{ margin: 0 }}>
          <AlertCircle size={18} className="ats-error-icon" />
          <span>{error}</span>
        </div>
      )}

      <div className="analyzer-actions-row">
        <button 
          type="button" 
          className="ats-cancel-btn"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>

        <button 
          type="button"
          className="analyze-btn" 
          onClick={handleAnalyze}
          disabled={loading || !file || !jobDescription}
        >
          {loading ? (
            <>
              <div className="ats-loader" style={{ width: '18px', height: '18px', borderWidth: '2px', margin: 0 }}></div>
              Analyzing...
            </>
          ) : (
            <>
              <Zap size={18} fill="currentColor" />
              Scan for Match Role
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderResultsView = () => {
    if (!results) return null;

    const strokeDashoffset = 440 - (440 * results.score) / 100;

    // Calculate issues
    const searchabilityIssues = [
      results.searchability.contactInfo.address,
      results.searchability.contactInfo.email,
      results.searchability.contactInfo.phone,
      results.searchability.summary,
      results.searchability.sectionHeadings.education,
      results.searchability.sectionHeadings.experience,
      results.searchability.jobTitleMatch.match,
      results.searchability.dateFormatting,
      results.searchability.educationMatch,
      results.fileType.isPdf
    ].filter(v => v === false).length;

    const skillIssues = results.hardSkills.filter(s => s.resumeCount === 0).length;

    return (
      <div className="analyzer-results-layout">
        {/* Sidebar */}
        <aside className="results-sidebar">
          {/* Match Rate Card */}
          <div className="match-rate-card">
            <h3 className="match-rate-title">Match Rate</h3>
            <div className="score-circle-container">
              <svg className="score-svg" width="160" height="160">
                <circle className="score-bg" cx="80" cy="80" r="70" />
                <circle 
                  className="score-fill" 
                  cx="80" 
                  cy="80" 
                  r="70" 
                  strokeDasharray="440"
                  strokeDashoffset={strokeDashoffset}
                />
              </svg>
              <div className="score-text">
                <span className="score-number">{results.score}<span className="score-percent">%</span></span>
              </div>
            </div>
            <button className="rescan-btn" onClick={resetAnalyzer}>
              Upload & rescan
            </button>
            <button 
              className="rescan-btn" 
              onClick={() => setShowRecommendationsModal(true)}
              style={{ marginTop: '10px', backgroundColor: '#8b5cf6', color: 'white', borderColor: '#8b5cf6' }}
            >
              💡 Recommendations for fix the resume
            </button>
          </div>

          {/* Navigation */}
          <div className="category-nav">
            <div 
              className={`category-item ${activeCategory === 'searchability' ? 'active' : ''}`}
              onClick={() => setActiveCategory('searchability')}
            >
              <div className="category-info">
                <ShieldCheck size={18} />
                <span>Searchability</span>
              </div>
              <span className={`category-score ${searchabilityIssues > 0 ? 'issue' : ''}`}>
                {searchabilityIssues} {searchabilityIssues === 1 ? 'issue' : 'issues'} to fix
              </span>
            </div>
            
            <div 
              className={`category-item ${activeCategory === 'hardskills' ? 'active' : ''}`}
              onClick={() => setActiveCategory('hardskills')}
            >
              <div className="category-info">
                <Layers size={18} />
                <span>Hard Skills</span>
              </div>
              <span className={`category-score ${skillIssues > 0 ? 'issue' : ''}`}>
                {skillIssues} {skillIssues === 1 ? 'issue' : 'issues'} to fix
              </span>
            </div>

            <div 
              className={`category-item ${activeCategory === 'softskills' ? 'active' : ''}`}
              onClick={() => setActiveCategory('softskills')}
            >
              <div className="category-info">
                <Zap size={18} />
                <span>Soft Skills</span>
              </div>
              <span className="category-score">{results.softSkills.score}% Match</span>
            </div>

            <div 
              className={`category-item ${activeCategory === 'tips' ? 'active' : ''}`}
              onClick={() => setActiveCategory('tips')}
            >
              <div className="category-info">
                <Settings size={18} />
                <span>Recruiter Tips</span>
              </div>
              <span className="category-score">4 Tips</span>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="results-main">
          <nav className="results-tabs">
            <div 
              className={`tab-item ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              Resume Report
            </div>
            <div 
              className={`tab-item ${activeTab === 'jd' ? 'active' : ''}`}
              onClick={() => setActiveTab('jd')}
            >
              Job Description
            </div>
          </nav>

          <div className="content-card">
            {activeTab === 'report' ? (
              <div className="report-content">
                {activeCategory === 'searchability' && (
                  <div className="section-searchability">
                    <div className="section-header">
                      <h2 className="section-title">Searchability <span className="badge-important">IMPORTANT</span></h2>
                    </div>
                    <p style={{ color: '#6b7280', marginBottom: '24px', fontStyle: 'italic', fontSize: '13px' }}>
                      Tip: Fix the red Xs to ensure your resume is easily searchable by recruiters and parsed correctly by the ATS.
                    </p>

                    <div className="tips-list">
                      {/* Contact Info */}
                      <div className="tip-row">
                        <span className="tip-label">Contact Information</span>
                        <div className="tip-content">
                          <div className="issue-item">
                            {results.searchability.contactInfo.address ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>We {results.searchability.contactInfo.address ? 'found' : 'did not find'} an address in your resume.</span>
                          </div>
                          <div className="issue-item">
                            {results.searchability.contactInfo.email ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>We {results.searchability.contactInfo.email ? 'found' : 'did not find'} an email in your resume.</span>
                          </div>
                          <div className="issue-item">
                            {results.searchability.contactInfo.phone ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>We {results.searchability.contactInfo.phone ? 'found' : 'did not find'} a phone number in your resume.</span>
                          </div>
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="tip-row">
                        <span className="tip-label">Summary</span>
                        <div className="tip-content">
                          <div className="issue-item">
                            {results.searchability.summary ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>{results.searchability.summary ? 'We found a summary section. Good job!' : 'We did not find a summary section. Adding one can help recruiters grasp your value.'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Section Headings */}
                      <div className="tip-row">
                        <span className="tip-label">Section Headings</span>
                        <div className="tip-content">
                          <div className="issue-item">
                            {results.searchability.sectionHeadings.education ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>We {results.searchability.sectionHeadings.education ? 'found' : 'did not find'} the education section.</span>
                          </div>
                          <div className="issue-item">
                            {results.searchability.sectionHeadings.experience ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>We {results.searchability.sectionHeadings.experience ? 'found' : 'did not find'} the work experience section.</span>
                          </div>
                        </div>
                      </div>

                      {/* Job Title Match */}
                      <div className="tip-row">
                        <span className="tip-label">Job Title Match</span>
                        <div className="tip-content">
                           <div className="issue-item">
                            {results.searchability.jobTitleMatch.match ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>{results.searchability.jobTitleMatch.match ? 'Your resume matches the job title.' : `The job title of '${results.searchability.jobTitleMatch.expectedTitle}' was not found in your resume.`}</span>
                          </div>
                        </div>
                      </div>

                      {/* Date Formatting */}
                      <div className="tip-row">
                        <span className="tip-label">Date Formatting</span>
                        <div className="tip-content">
                           <div className="issue-item">
                            {results.searchability.dateFormatting ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>{results.searchability.dateFormatting ? 'ATS and recruiters prefer specific date formatting. Yours looks good.' : 'Consider using consistent MM/YYYY or Month YYYY formatting.'}</span>
                          </div>
                        </div>
                      </div>

                       {/* File Type */}
                       <div className="tip-row">
                        <span className="tip-label">File Check</span>
                        <div className="tip-content">
                           <div className="issue-item">
                            {results.fileType.isPdf ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>{results.fileType.isPdf ? 'You are using a .pdf resume, which is preferred.' : 'Consider using PDF format for better parsing.'}</span>
                          </div>
                          <div className="issue-item">
                            {results.fileType.noSpecialChars ? <CheckCircle size={16} className="issue-icon success" /> : <X size={16} className="issue-icon error" />}
                            <span>{results.fileType.noSpecialChars ? 'Your file name is clean from special characters.' : 'Remove special characters from file name.'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeCategory === 'hardskills' && (
                  <div className="section-skills">
                    <div className="section-header">
                      <h2 className="section-title">Hard Skills <span className="badge-important">HIGH SCORE IMPACT</span></h2>
                    </div>
                    
                    <div className="skills-tabs" style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '20px' }}>
                      <div 
                        className={`skills-tab ${skillsTab === 'comparison' ? 'active' : ''}`}
                        onClick={() => setSkillsTab('comparison')}
                        style={{ padding: '10px 20px', cursor: 'pointer', borderBottom: skillsTab === 'comparison' ? '2px solid #3b5bfd' : 'none', color: skillsTab === 'comparison' ? '#3b5bfd' : '#6b7280', fontWeight: 'bold' }}
                      >
                        Skills Comparison
                      </div>
                      <div 
                        className={`skills-tab ${skillsTab === 'highlighted' ? 'active' : ''}`}
                        onClick={() => setSkillsTab('highlighted')}
                        style={{ padding: '10px 20px', cursor: 'pointer', borderBottom: skillsTab === 'highlighted' ? '2px solid #3b5bfd' : 'none', color: skillsTab === 'highlighted' ? '#3b5bfd' : '#6b7280', fontWeight: 'bold' }}
                      >
                        Highlighted Skills
                      </div>
                    </div>

                    {skillsTab === 'comparison' ? (
                      <div className="skills-table-wrapper" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontSize: '14px' }}>
                              <th style={{ padding: '12px' }}>Skill</th>
                              <th style={{ padding: '12px' }}>Resume</th>
                              <th style={{ padding: '12px' }}>Job Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.hardSkills.map((s, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px', fontWeight: '500' }}>{s.skill}</td>
                                <td style={{ padding: '12px' }}>
                                  {s.resumeCount > 0 ? (
                                    <span style={{ color: '#3b5bfd', fontWeight: 'bold' }}>{s.resumeCount}</span>
                                  ) : (
                                    <X size={16} color="#ef4444" />
                                  )}
                                </td>
                                <td style={{ padding: '12px', color: '#4b5563' }}>{s.jdCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="highlighted-view">
                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                            {results.hardSkills.map((s, idx) => (
                              <span key={idx} className={`skill-tag ${s.resumeCount > 0 ? 'matched' : 'missing'}`}>
                                {s.skill}
                              </span>
                            ))}
                         </div>
                         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="text-box">
                              <h4 style={{ marginBottom: '10px' }}>Resume:</h4>
                              <div style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', maxHeight: '400px', overflowY: 'auto', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                 {/* Simple highlighting */}
                                 {highlightText(
                                   results.rawTexts.resume ? results.rawTexts.resume.replace(/\[object Object\]/g, '') : '', 
                                   results.hardSkills
                                 )}
                              </div>
                            </div>
                            <div className="text-box">
                              <h4 style={{ marginBottom: '10px' }}>Job Description:</h4>
                              <div style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', maxHeight: '400px', overflowY: 'auto', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                 {highlightText(results.rawTexts.jd, results.hardSkills)}
                              </div>
                            </div>
                         </div>
                      </div>
                    )}
                  </div>
                )}

                {activeCategory === 'softskills' && (
                  <div className="section-skills">
                    <h2 className="section-title">Soft Skills Analysis</h2>
                    <div className="tip-row" style={{ marginTop: '20px' }}>
                      <span className="tip-label">Top Attributes</span>
                      <div className="tip-content">
                        <div className="skills-grid">
                          {results.softSkills.matched.map((skill, idx) => (
                            <span key={idx} className="skill-tag matched">{skill}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeCategory === 'tips' && (
                  <div className="section-tips">
                    <h2 className="section-title">Recruiter Tips</h2>
                    <div className="tips-list" style={{ marginTop: '20px' }}>
                       <div className="issue-item">
                        <Zap size={18} color="#f59e0b" fill="#fde68a" />
                        <span><strong>Add measurable results:</strong> Include numbers like "increased sales by 20%" to catch a recruiter's eye.</span>
                      </div>
                      <div className="issue-item">
                        <Zap size={18} color="#f59e0b" fill="#fde68a" />
                        <span><strong>Keep it concise:</strong> Your resume length is good, but try to avoid wordy paragraphs.</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="jd-tab-content">
                {jobDescription}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  };

  const renderRecommendationsContent = () => {
    if (!results) return null;

    const matchedHardSkills = results.hardSkills.filter(s => s.resumeCount > 0);
    const missingHardSkills = results.hardSkills.filter(s => s.resumeCount === 0);

    // Fallback values if recommendations object doesn't exist yet
    const recs = results.recommendations || {};
    const techMatch = recs.technicalMatchScore ?? (results.hardSkills.length > 0 
      ? Math.round((matchedHardSkills.length / results.hardSkills.length) * 100) 
      : 0);
    const networkMatch = recs.networkingExperienceScore ?? "TBD";
    const potentialScoreStr = recs.improvementPotential ?? `${Math.min(100, results.score + (missingHardSkills.length * 2) + 5)} - 100/100`;

    const topSkills = [...matchedHardSkills].sort((a, b) => b.jdCount - a.jdCount).slice(0, 3);
    
    // Strengths & Weaknesses
    const strengths = recs.strengths || [];
    if (!recs.strengths) {
      if (techMatch > 70) strengths.push("Strong matching technical skills");
      if (results.searchability?.summary) strengths.push("Includes professional summary");
      if (results.fileType?.isPdf) strengths.push("Correct resume file format (PDF)");
    }

    const weaknesses = recs.primaryGaps || [];
    if (!recs.primaryGaps) {
      if (missingHardSkills.length > 0) weaknesses.push(`Missing ${missingHardSkills.length} mandatory technical keywords`);
      if (results.searchability && (!results.searchability.contactInfo.phone || !results.searchability.contactInfo.email)) weaknesses.push("Missing contact information");
      if (results.searchability && !results.searchability.jobTitleMatch.match) weaknesses.push("Job title does not match JD");
    }

    const certGaps = recs.certificationsGap || [];
    if (!recs.certificationsGap) {
        const certKeywords = ["CCNA", "CCNP", "CCIE", "AWS", "Azure", "GCP", "Certification", "ITIL", "PMP"];
        missingHardSkills.filter(s => certKeywords.some(cert => s.skill.toLowerCase().includes(cert.toLowerCase()))).forEach(s => certGaps.push(s.skill));
    }

    const boostUpPoints = recs.boostUpPoints || {
        professionalSummary: missingHardSkills.slice(0, 3).map(s => `Extensive experience implementing ${s.skill} solutions.`),
        technicalSkills: missingHardSkills.map(s => s.skill),
        professionalExperience: missingHardSkills.slice(0, 5).map(s => `Configured and managed ${s.skill} environments.`)
    };

    const whereToAdd = recs.whereToAdd || [
        { section: "Professional Summary", skills: missingHardSkills.slice(0, 3).map(s => s.skill).join(', ') || 'N/A' },
        { section: "Technical Skills", skills: missingHardSkills.slice(3, 6).map(s => s.skill).join(', ') || 'N/A' },
        { section: "Recent Experience", skills: missingHardSkills.slice(6, 10).map(s => s.skill).join(', ') || 'N/A' }
    ];

    return (
      <div ref={reportRef} style={{ padding: '0', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        
        {/* 1. ATS Match Score */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>1. ATS Match Score (Out of 100)</h3>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Overall ATS Score: {results.score}/100</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Category</th>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Score</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Technical Skills Match</td>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{techMatch}/100</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Networking Experience</td>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{networkMatch}/100</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb', fontWeight: 'bold' }}>Overall ATS Score</td>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb', fontWeight: 'bold' }}>{results.score}/100</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 2. Top 3 Matching Skills */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>2. Top 3 Matching Skills</h3>
          {topSkills.length > 0 ? (
            <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6' }}>
               {topSkills.map((s, idx) => <li key={idx}>{s.skill}</li>)}
            </ul>
          ) : (
            <p style={{ fontSize: '14px', color: '#6b7280' }}>No matching technical skills found.</p>
          )}
        </div>

        {/* 3. Relevant Skills Summary */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>3. Relevant Skills Summary</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Mandatory Skills (As listed in JD)</th>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}># of Years Experience</th>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Candidate's Relevant Hands-on Experience</th>
              </tr>
            </thead>
            <tbody>
              {matchedHardSkills.slice(0, 5).map((s, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{s.skill}</td>
                  <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{s.yearsExperience || 'TBD'}</td>
                  <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{s.evidence || `Evidenced in resume (${s.resumeCount} mentions)`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 4. Missing Skills */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>4. Missing Skills</h3>
          <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>The resume is technically strong but the following JD keywords are missing or weakly represented:</p>
          {missingHardSkills.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Skill</th>
                  <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {missingHardSkills.map((s, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{s.skill}</td>
                    <td style={{ padding: '8px', border: '1px solid #e5e7eb', color: '#dc2626' }}>❌ Missing</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#2563eb', fontSize: '14px', marginTop: '16px', fontWeight: '500' }}>No missing mandatory skills!</p>
          )}
        </div>

        {/* 5. ATS Boost-up Points */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>5. ATS Boost-up Points</h3>
          <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '12px' }}>If the candidate has actual experience with these activities, add bullets like the following to improve ATS relevance:</p>
          
          <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Professional Summary</h4>
          {boostUpPoints.professionalSummary?.length > 0 ? (
            <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
              {boostUpPoints.professionalSummary.map((pt, idx) => <li key={idx}>{pt}</li>)}
            </ul>
          ) : <p style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic', marginBottom: '16px' }}>N/A</p>}

          <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Technical Skills Section</h4>
          {boostUpPoints.technicalSkills?.length > 0 ? (
            <>
              <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>Add:</p>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
                {boostUpPoints.technicalSkills.map((pt, idx) => <li key={idx}>{pt}</li>)}
              </ul>
            </>
          ) : <p style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic', marginBottom: '16px' }}>N/A</p>}

          <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Professional Experience</h4>
          {boostUpPoints.professionalExperience?.length > 0 ? (
            <>
              <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>Include bullets such as:</p>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6' }}>
                {boostUpPoints.professionalExperience.map((pt, idx) => <li key={idx}>{pt}</li>)}
              </ul>
            </>
          ) : <p style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>N/A</p>}
        </div>

        {/* 6. Where to Add ATS Keywords */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>6. Where to Add the Missing Skills</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Resume Section</th>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Skills to Add</th>
              </tr>
            </thead>
            <tbody>
              {whereToAdd.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{item.section}</td>
                  <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{item.skills || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 7. Certifications Gap */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>7. Certifications Gap</h3>
          <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px', fontWeight: 'bold' }}>Preferred in JD</p>
          <ul style={{ listStyleType: 'none', paddingLeft: '0', color: '#dc2626', fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' }}>
            {certGaps.length > 0 ? certGaps.map((cert, idx) => (
              <li key={idx}>❌ {cert}</li>
            )) : <li style={{ color: '#2563eb' }}>No specific certifications missing.</li>}
          </ul>
        </div>

        {/* 8. ATS Improvement Potential */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>8. ATS Improvement Potential</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>Current</th>
                <th style={{ padding: '8px', border: '1px solid #e5e7eb' }}>After ATS Optimization</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb', fontWeight: 'bold' }}>{results.score}/100</td>
                <td style={{ padding: '8px', border: '1px solid #e5e7eb', color: '#2563eb', fontWeight: 'bold' }}>{potentialScoreStr}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 9. Final Recommendation */}
        <div>
          <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '12px', color: '#111827' }}>9. Final Recommendation</h3>
          <p style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 16px 0', color: '#111827' }}>
            Overall ATS Score: {results.score}/100
          </p>
          
          <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Strengths:</h4>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
            {strengths.map((str, idx) => <li key={idx}>{str}</li>)}
            {strengths.length === 0 && <li style={{ color: '#6b7280' }}>No specific strengths highlighted</li>}
          </ul>

          <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Primary Gaps Affecting ATS Score:</h4>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
            {weaknesses.map((wk, idx) => <li key={idx}>{wk}</li>)}
            {weaknesses.length === 0 && <li style={{ color: '#6b7280' }}>No significant weaknesses</li>}
          </ul>

          <p style={{ fontSize: '14px', color: '#4b5563', margin: 0, fontStyle: 'italic' }}>
            If the candidate has hands-on experience with these areas, adding the recommended keywords and project bullets can realistically increase the ATS score to {potentialScoreStr}.
          </p>
        </div>

      </div>
    );
  };

  return (
    <div className="analyzer-container">
      {results && (
        <button className="ats-back-btn" onClick={resetAnalyzer} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', marginBottom: '10px' }}>
          <ArrowLeft size={16} /> Back to scanner
        </button>
      )}
      
      {!results ? renderInputView() : renderResultsView()}

      {showRecommendationsModal && results && (
        <div className="recruitment-modal" onClick={() => setShowRecommendationsModal(false)} style={{ zIndex: 1000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="recruitment-modal-content" style={{ maxWidth: '800px', width: '100%', maxHeight: '80vh', overflowY: 'auto', backgroundColor: '#fff', borderRadius: '8px', padding: '20px' }} onClick={e => e.stopPropagation()}>
            <div className="recruitment-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Recommendations to Fix Resume</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '40px', marginRight:'40px' }}>
                <button 
                  onClick={exportToWord} 
                  style={{ padding: '8px 16px', backgroundColor: '#3b5bfd', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background-color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b5bfd'}
                >
                  <FileText size={16} /> Download
                </button>
                <span className="recruitment-close" onClick={() => setShowRecommendationsModal(false)} style={{ cursor: 'pointer', fontSize: '28px', fontWeight: 'bold', color: '#6b7280', lineHeight: '1' }}>&times;</span>
              </div>
            </div>
            <div className="recruitment-modal-body">
              {renderRecommendationsContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
